import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ProgramEnrollmentFields from '../Enrollment/ProgramEnrollmentFields';
import { DR_API, DEVICE_TYPES, errMsg, patientLabel, toInputDate, typeLabel } from './deviceRequestApi';

const ICD10 = /^[A-TV-Z][0-9][0-9A-Z](?:\.[0-9A-Z]{1,4})?$/;
const today = () => new Date().toISOString().slice(0, 10);

const fromRequest = (r) => ({
    patientId: r ? String(r.patientId?._id || r.patientId) : '',
    deviceTypes: r?.deviceTypes || [],
    prescribingProviderId: r?.prescribingProviderId ? String(r.prescribingProviderId) : '',
    datePrescribed: toInputDate(r?.datePrescribed),
    diagnosisCodes: r?.diagnosisCodes || [],
    consentGiven: r?.consent?.consentGiven === true ? 'yes' : r?.consent?.consentGiven === false ? 'no' : '',
    consentDate: toInputDate(r?.consent?.consentDate),
    consentMethod: r?.consent?.consentMethod || '',
    programStartDate: toInputDate(r?.programStartDate),
    deliveryMethod: r?.delivery?.method || '',
    addressConfirmed: !!r?.delivery?.addressConfirmed,
    address: r?.delivery?.address || '',
    line2: r?.delivery?.line2 || '',
    city: r?.delivery?.city || '',
    state: r?.delivery?.state || '',
    zipCode: r?.delivery?.zipCode || '',
    phone: r?.phone || '',
    phoneVerified: r?.phoneVerified === true ? 'yes' : r?.phoneVerified === false ? 'no' : '',
    notes: r?.notes || '',
});

/** Client-side mirror of the server's required-field rules (the server re-validates). */
export const validateForm = (f) => {
    const e = [];
    if (!f.patientId) e.push('Patient is required');
    if (!f.deviceTypes.length) e.push('Select at least one device type');
    if (!f.prescribingProviderId) e.push('Prescribing provider is required');
    if (!f.datePrescribed) e.push('Date prescribed is required');
    else if (f.datePrescribed > today()) e.push('Date prescribed cannot be in the future');
    if (!f.diagnosisCodes.length) e.push('At least one qualifying diagnosis code is required');
    if (f.diagnosisCodes.some((c) => !ICD10.test(c))) e.push('Diagnosis codes must be ICD-10 (e.g. I10, E11.9)');
    if (!f.consentGiven) e.push('RPM consent (yes/no) is required');
    if (f.consentGiven === 'yes' && (!f.consentDate || !f.consentMethod)) e.push('Consent date and method are required');
    if (!f.deliveryMethod) e.push('Delivery (in clinic / ship) is required');
    if (f.deliveryMethod === 'ship') {
        if (!f.address || !f.city || !f.state || !f.zipCode) e.push('Shipping address is incomplete');
        if (!f.addressConfirmed) e.push('Confirm the shipping address');
    }
    if (!f.phoneVerified) e.push('Phone verified (yes/no) is required');
    return e;
};

export const toPayload = (f) => ({
    deviceTypes: f.deviceTypes,
    prescribingProviderId: f.prescribingProviderId,
    datePrescribed: f.datePrescribed,
    diagnosisCodes: f.diagnosisCodes,
    consent: {
        consentGiven: f.consentGiven === 'yes',
        consentDate: f.consentGiven === 'yes' ? f.consentDate : null,
        consentMethod: f.consentGiven === 'yes' ? f.consentMethod : null,
    },
    programStartDate: f.programStartDate || null,
    delivery: {
        method: f.deliveryMethod,
        addressConfirmed: f.deliveryMethod === 'ship' ? f.addressConfirmed : false,
        ...(f.deliveryMethod === 'ship'
            ? { address: f.address, line2: f.line2, city: f.city, state: f.state, zipCode: f.zipCode }
            : {}),
    },
    phone: f.phone,
    phoneVerified: f.phoneVerified === 'yes',
    notes: f.notes,
});

/**
 * RPM Device Request fields. mode: 'create' (patient picker + prefill), 'edit' or 'resubmit'.
 * `onSubmit(payload, acknowledgeDuplicates)` must return a promise; a 409 with details.warnings shows the duplicate warning.
 */
const DeviceRequestForm = ({ mode = 'create', request, presetPatientId, submitLabel = 'Submit request', onSubmit, onCancel, disabled }) => {
    const [form, setForm] = useState(() => ({ ...fromRequest(request), patientId: presetPatientId || fromRequest(request).patientId }));
    const [patient, setPatient] = useState(request?.patient || null);
    const [prefill, setPrefill] = useState(null);
    const [providers, setProviders] = useState([]);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [codeInput, setCodeInput] = useState('');
    const [errors, setErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const orgId = patient?.organizationId || request?.organizationId;
    useEffect(() => {
        if (!orgId) return;
        axios
            .get(`${DR_API}/providers`, { params: { organizationId: String(orgId?._id || orgId) } })
            .then((res) => setProviders(res.data.providers || []))
            .catch(() => setProviders([]));
    }, [orgId]);

    useEffect(() => {
        if (!form.patientId) return;
        axios
            .get(`${DR_API}/patients/${form.patientId}/prefill`)
            .then(({ data }) => {
                setPrefill(data);
                setPatient(data.patient);
                if (mode !== 'create') return;
                const p = data.patient || {};
                setForm((f) => ({
                    ...f,
                    diagnosisCodes: f.diagnosisCodes.length ? f.diagnosisCodes : data.diagnosisCodes || [],
                    prescribingProviderId: f.prescribingProviderId || (data.assignedDoctorId ? String(data.assignedDoctorId) : ''),
                    phone: f.phone || p.mobileNo || p.phone1 || '',
                    address: f.address || p.address || '',
                    line2: f.line2 || p.line2 || '',
                    city: f.city || p.city || '',
                    state: f.state || p.state || '',
                    zipCode: f.zipCode || p.zipCode || '',
                    ...(data.rpmConsent && data.rpmConsent.consentGiven && !f.consentGiven
                        ? {
                              consentGiven: 'yes',
                              consentDate: toInputDate(data.rpmConsent.consentDate),
                              consentMethod: data.rpmConsent.consentMethod || '',
                          }
                        : {}),
                }));
            })
            .catch((err) => setErrors([errMsg(err)]));
    }, [form.patientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const runSearch = async () => {
        try {
            const { data } = await axios.get(`${DR_API}/patients`, { params: { search, limit: 15 } });
            setResults(data.patients || []);
        } catch (err) {
            setErrors([errMsg(err)]);
        }
    };

    const liveWarnings = useMemo(() => {
        if (!prefill) return [];
        const w = [];
        (prefill.openRequests || [])
            .filter((r) => String(r._id) !== String(request?._id))
            .forEach((r) => r.deviceTypes.filter((t) => form.deviceTypes.includes(t)).forEach((t) => w.push(`Open request ${r.requestNo} (${r.status}) already asks for a ${typeLabel(t)}`)));
        (prefill.activeDevices || [])
            .filter((d) => form.deviceTypes.includes(d.deviceType))
            .forEach((d) => w.push(`Patient already has an active ${typeLabel(d.deviceType)} (device ${d.deviceId})`));
        return w;
    }, [prefill, form.deviceTypes, request]);

    const addCode = () => {
        const parts = codeInput.toUpperCase().split(/[\s,;]+/).filter(Boolean);
        if (!parts.length) return;
        set('diagnosisCodes', [...new Set([...form.diagnosisCodes, ...parts])]);
        setCodeInput('');
    };

    const submit = async (ack = false) => {
        const e = validateForm(form);
        setErrors(e);
        if (e.length) return;
        setBusy(true);
        try {
            await onSubmit({ ...toPayload(form), ...(mode === 'create' ? { patientId: form.patientId } : {}) }, ack);
            setWarnings([]);
        } catch (err) {
            if (err?.response?.status === 409 && err.response.data?.details?.warnings) {
                setWarnings(err.response.data.details.warnings.map((w) => w.message));
            } else setErrors([errMsg(err)]);
        } finally {
            setBusy(false);
        }
    };

    const consentValues = { RPM: { startDate: form.programStartDate, consentGiven: form.consentGiven, consentDate: form.consentDate, consentMethod: form.consentMethod } };
    const onConsent = (program, field, value) => set(field === 'startDate' ? 'programStartDate' : field, value);
    const off = disabled || busy;

    return (
        <form onSubmit={(e) => { e.preventDefault(); submit(false); }} data-testid="device-request-form">
            {mode === 'create' && !presetPatientId && (
                <div className="mb-3">
                    <label className="form-label" htmlFor="dr-patient-search">Patient *</label>
                    {patient ? (
                        <div>
                            <strong>{patientLabel(patient)}</strong> {patient.emrId && <span className="text-muted">EHR-ID {patient.emrId}</span>}{' '}
                            <button type="button" className="btn btn-link btn-sm" onClick={() => { setPatient(null); setPrefill(null); set('patientId', ''); }}>change</button>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex">
                                <input id="dr-patient-search" className="form-control" placeholder="Search name or EHR-ID" value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }} />
                                <button type="button" className="btn btn-outline-primary btn-sm ms-2 ml-2" onClick={runSearch}>Search</button>
                            </div>
                            <ul className="list-group mt-1">
                                {results.map((p) => (
                                    <li key={p._id} className="list-group-item list-group-item-action" role="button" onClick={() => { setPatient(p); set('patientId', p._id); setResults([]); }}>
                                        {patientLabel(p)} {p.emrId && <span className="text-muted">· {p.emrId}</span>}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
            {(mode !== 'create' || presetPatientId) && patient && (
                <div className="mb-3"><strong>Patient:</strong> {patientLabel(patient)} {patient.emrId && <span className="text-muted">EHR-ID {patient.emrId}</span>}</div>
            )}

            <fieldset className="mb-3" disabled={off}>
                <legend className="form-label fs-6">Device type(s) *</legend>
                {DEVICE_TYPES.map((d) => (
                    <label key={d.value} className="me-3 mr-3">
                        <input type="checkbox" checked={form.deviceTypes.includes(d.value)}
                            onChange={(e) => set('deviceTypes', e.target.checked ? [...form.deviceTypes, d.value] : form.deviceTypes.filter((t) => t !== d.value))} />{' '}
                        {d.label}
                    </label>
                ))}
            </fieldset>
            {liveWarnings.length > 0 && (
                <div className="alert alert-warning py-2" data-testid="dr-live-warnings">
                    {liveWarnings.map((w) => <div key={w}>{w}</div>)}
                </div>
            )}

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="dr-provider">Prescribing provider *</label>
                    <select id="dr-provider" className="form-control" disabled={off} value={form.prescribingProviderId} onChange={(e) => set('prescribingProviderId', e.target.value)}>
                        <option value="">Select provider</option>
                        {providers.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="dr-date">Date prescribed *</label>
                    <input id="dr-date" type="date" className="form-control" max={today()} disabled={off} value={form.datePrescribed} onChange={(e) => set('datePrescribed', e.target.value)} />
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label" htmlFor="dr-dx">Qualifying diagnosis code(s) * <span className="text-muted small">(ICD-10, from the patient&apos;s diagnoses)</span></label>
                <div className="mb-1">
                    {form.diagnosisCodes.map((c) => (
                        <span key={c} className={`badge ${ICD10.test(c) ? 'badge-info' : 'badge-danger'} me-1 mr-1`}>
                            {c} {!off && <button type="button" className="btn btn-sm p-0 text-white" aria-label={`remove ${c}`} onClick={() => set('diagnosisCodes', form.diagnosisCodes.filter((x) => x !== c))}>×</button>}
                        </span>
                    ))}
                </div>
                {prefill?.diseases && <div className="small text-muted mb-1">Patient diagnoses: {prefill.diseases}</div>}
                <div className="d-flex">
                    <input id="dr-dx" className="form-control" placeholder="Add code e.g. I10" disabled={off} value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCode(); } }} />
                    <button type="button" className="btn btn-outline-secondary btn-sm ms-2 ml-2" disabled={off} onClick={addCode}>Add</button>
                </div>
            </div>

            <fieldset className="mb-2" disabled={off}>
                <legend className="form-label fs-6">RPM consent *</legend>
                <ProgramEnrollmentFields programs={['RPM']} values={consentValues} onChange={onConsent} />
            </fieldset>

            <fieldset className="mb-3" disabled={off}>
                <legend className="form-label fs-6">Delivery *</legend>
                <label className="me-3 mr-3"><input type="radio" name="dr-delivery" checked={form.deliveryMethod === 'clinic'} onChange={() => set('deliveryMethod', 'clinic')} /> In clinic</label>
                <label><input type="radio" name="dr-delivery" checked={form.deliveryMethod === 'ship'} onChange={() => set('deliveryMethod', 'ship')} /> Ship to patient</label>
                {form.deliveryMethod === 'ship' && (
                    <div className="row mt-2">
                        {[['address', 'Address', 6], ['line2', 'Line 2', 6], ['city', 'City', 5], ['state', 'State', 3], ['zipCode', 'ZIP', 4]].map(([k, l, w]) => (
                            <div key={k} className={`col-md-${w} mb-2`}>
                                <label className="form-label small" htmlFor={`dr-${k}`}>{l}</label>
                                <input id={`dr-${k}`} className="form-control" value={form[k]} onChange={(e) => set(k, e.target.value)} />
                            </div>
                        ))}
                        <div className="col-12">
                            <label><input type="checkbox" checked={form.addressConfirmed} onChange={(e) => set('addressConfirmed', e.target.checked)} /> Address confirmed with the patient</label>
                        </div>
                    </div>
                )}
            </fieldset>

            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="dr-phone">Phone</label>
                    <input id="dr-phone" className="form-control" disabled={off} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="dr-phone-verified">Phone verified *</label>
                    <select id="dr-phone-verified" className="form-control" disabled={off} value={form.phoneVerified} onChange={(e) => set('phoneVerified', e.target.value)}>
                        <option value="">Select</option><option value="yes">Yes</option><option value="no">No</option>
                    </select>
                </div>
            </div>
            <div className="mb-3">
                <label className="form-label" htmlFor="dr-notes">Notes</label>
                <textarea id="dr-notes" className="form-control" rows={2} maxLength={2000} disabled={off} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>

            {errors.length > 0 && (
                <div className="alert alert-danger py-2" role="alert">{errors.map((e) => <div key={e}>{e}</div>)}</div>
            )}
            {warnings.length > 0 && (
                <div className="alert alert-warning py-2" data-testid="dr-dup-warnings">
                    <strong>Possible duplicate:</strong>
                    {warnings.map((w) => <div key={w}>{w}</div>)}
                    <button type="button" className="btn btn-warning btn-sm mt-2" disabled={off} onClick={() => submit(true)}>Submit anyway</button>
                </div>
            )}
            {!disabled && (
                <div>
                    <button type="submit" className="btn btn-primary" disabled={off}>{busy ? 'Saving…' : submitLabel}</button>
                    {onCancel && <button type="button" className="btn btn-link" onClick={onCancel}>Cancel</button>}
                </div>
            )}
        </form>
    );
};

export default DeviceRequestForm;
